import { MEDIA_TYPE } from '../../base/media/constants';
import logger from './logger';
class RemoteFarEndMix {
    _ac;
    _mixGain;
    _dest;
    _sources = new Map();
    _resumeTimer = null;
    _tapMode;
    constructor(tapMode = 'capture') {
        this._tapMode = tapMode;
        this._ac = new AudioContext();
        this._mixGain = this._ac.createGain();
        this._dest = this._ac.createMediaStreamDestination();
        this._mixGain.connect(this._dest);
        // 'element' tap mode: createMediaElementSource reroutes the <audio>
        // elements' sound INTO this graph (they no longer reach the OS
        // speaker directly), so mixGain must also feed ac.destination to stay
        // audible.  far reference and speaker output then share one clock —
        // this is what fixes AEC on devices where captureStream() jitter
        // makes the far/mic time relationship wobble (observed ±30 ms on
        // Android → ERLE 1-3 dB; same engine with an element tap → 18+ dB).
        if (tapMode === 'element') {
            this._mixGain.connect(this._ac.destination);
        }
        // mixGain feeds ONLY the AEC far-end reference — it is deliberately
        // NOT connected to ac.destination.
        //
        // Audible remote playback is done by the per-source <audio> elements in
        // sync() (volume=1, native — robust/no crackle). Those SAME elements are
        // captureStream()-tapped into this mixGain, so the far reference is
        // bit-identical to what the user hears (required for the NKF filter to
        // actually cancel — a separate silent tap only reached ~4-5 dB ERLE).
        // Routing playback itself through this AudioContext (mixGain ->
        // ac.destination) is what crackled on low-powered PCs, so it is avoided.
        // mixGain stays live because it is pulled by _dest (a
        // MediaStreamAudioDestinationNode) above.
        // Anchor: always keep at least one source connected so the
        // destination's MediaStream has a live audio track even when no
        // remote participant has joined yet.
        const anchor = this._ac.createConstantSource();
        anchor.offset.value = 0;
        anchor.connect(this._mixGain);
        anchor.start();
        // Autoplay policy starves the far-end reference in TWO ways: it can
        // leave the AC 'suspended', AND — even when the AC is already
        // 'running' — it keeps the hidden <audio> source elements paused
        // until a user gesture.  Either one yields far-peak=0 / ERLE=- ("AEC
        // off until I press a button": the button press is just the gesture).
        // So: install a one-shot gesture unlock (NOT gated on AC state) and
        // poll as a fallback, replaying the source elements each tick.
        this._installGestureUnlock();
        this._resumeTimer = setInterval(() => {
            void this._ac.resume().catch(() => { });
            this._playAllSources();
            if (this._ac.state === 'running' && !this._anySourcePaused()) {
                if (this._resumeTimer !== null) {
                    clearInterval(this._resumeTimer);
                    this._resumeTimer = null;
                }
            }
        }, 800);
    }
    /**
     * Replay any hidden source <audio> elements the autoplay policy paused.
     * A paused element decodes nothing, so its captureStream() tap is silent
     * and the far-end reference stays at zero.
     */
    _playAllSources() {
        for (const [id, entry] of this._sources) {
            if (entry.audioEl?.paused) {
                void entry.audioEl.play().catch(() => { });
            }
            // A tap that never went live (silent captureStream race) is picked
            // up here and rebuilt — replaying the element alone won't revive an
            // already-latched silent track.
            if (!entry.connected && !entry.timer) {
                this._attachTap(id, entry);
            }
        }
    }
    /**
     * True while the far reference is still starved: any source <audio> is
     * paused, OR its tap has not gone live yet (silent captureStream). Keeps
     * the resume/re-tap poll running until far is genuinely flowing.
     */
    _anySourcePaused() {
        for (const entry of this._sources.values()) {
            if (entry.audioEl?.paused || !entry.connected) {
                return true;
            }
        }
        return false;
    }
    /**
     * One-shot user-gesture handler that resumes the AC and replays every
     * source element.  Unlike {@link resume} this is NOT gated on the AC being
     * 'suspended' — on strict devices the AC is already 'running' yet the
     * source <audio> elements stay paused until a gesture.  After the first
     * gesture the page is "unlocked" so later sources created by sync() play
     * on their own.
     */
    _installGestureUnlock() {
        if (typeof document === 'undefined') {
            return;
        }
        const handler = () => {
            void this._ac.resume().catch(() => { });
            this._playAllSources();
            document.removeEventListener('click', handler, true);
            document.removeEventListener('keydown', handler, true);
            document.removeEventListener('touchstart', handler, true);
        };
        document.addEventListener('click', handler, true);
        document.addEventListener('keydown', handler, true);
        document.addEventListener('touchstart', handler, true);
    }
    /**
     * Tap AudioTracksContainer's <audio> element via createMediaElementSource
     * so the AEC sees the same continuous, browser-buffered audio stream
     * that the user actually hears.
     *
     * Why MES on the existing <audio> element (not createMediaStreamSource
     * on the raw peer-connection stream)?  Chrome's
     * createMediaStreamSource(remoteStream) delivers samples with pull-mode
     * jitter — we observed silent frames interspersed with audio frames
     * even while the user clearly heard continuous remote audio.  NKF's
     * adaptive filter cannot converge with intermittent reference.
     *
     * createMediaElementSource on an HTMLMediaElement is buffered by the
     * browser's media pipeline and delivers samples continuously, exactly
     * matching what plays through the speaker.  Once we tap the element,
     * its audio is rerouted through Web Audio (no longer plays directly
     * to the OS speaker) — our `mixGain → ac.destination` becomes the
     * sole audible path, sample-identical to the AEC reference.
     *
     * AudioTracksContainer renders these elements asynchronously via
     * React, so we retry briefly until the DOM node exists.
     */
    /**
     * The MediaStream every effect should consume as its far-end input.
     */
    getFarEndStream() {
        return this._dest.stream;
    }
    /**
     * Direct AudioNode handle to the mixed remote audio.  Effects that can
     * share this AC connect to this node instead of going through a
     * MediaStream — single-AC routing avoids the Chrome cross-AC delivery
     * bugs that plague createMediaStreamSource on remote peer streams.
     */
    getFarEndNode() {
        return this._mixGain;
    }
    /**
     * The AudioContext that owns the mix bus.  Effects that want to share
     * this AC (recommended) should construct their workletNodes against it.
     */
    getAudioContext() {
        return this._ac;
    }
    /**
     * Build the captureStream() tap for a source and connect it to mixGain
     * ONLY once its audio track actually carries frames (the `unmute` event).
     *
     * A tap created while the <audio> is not yet decoding produces a
     * live-but-silent track: far-peak stays 0, nothing is cancelled, and the
     * remote voice loops back out the mic.  Whether that happens is a race
     * between the async play() and this synchronous tap, so it manifests
     * "case by case".  We therefore defer the connect until unmute, and if it
     * never fires we rebuild the tap (bounded), falling back to the raw remote
     * stream as a last resort so far is at least non-zero.
     */
    _attachTap(id, entry) {
        // The captured track only unmutes once the element is actually playing.
        void entry.audioEl.play().catch(() => { });
        // 'element' mode: read the remote stream with createMediaStreamSource
        // and let mixGain → ac.destination be the SOLE audible path, so the
        // speaker output and the far reference are the same samples from the
        // same node — any jitter hits both identically, alignment cannot
        // wobble.  (createMediaElementSource is NOT usable here: on Chrome a
        // srcObject <audio> tapped that way yields pure silence — verified on
        // device, far-peak stayed 0.)  The muted element created in sync()
        // keeps pulling the stream, without which Chrome delivers no samples
        // to a MediaStreamSource on a remote WebRTC stream.
        if (this._tapMode === 'element') {
            try {
                entry.src = this._ac.createMediaStreamSource(entry.stream);
                entry.src.connect(this._mixGain);
                entry.connected = true;
                if (entry.timer) {
                    clearTimeout(entry.timer);
                    entry.timer = null;
                }
                logger.info(`[nkf-aec/farend] source ${id} tap LIVE (element/stream-source)`);
                return;
            }
            catch (e) {
                logger.warn(`[nkf-aec/farend] source ${id} stream-source tap failed — falling back to capture`, e);
                try {
                    entry.audioEl.muted = false;
                }
                catch { /* ignore */ }
            }
        }
        const canCapture = typeof entry.audioEl.captureStream === 'function';
        const tapStream = canCapture
            ? entry.audioEl.captureStream()
            : entry.stream;
        const track = tapStream.getAudioTracks()[0];
        const wire = () => {
            if (entry.connected) {
                return;
            }
            try {
                entry.src?.disconnect();
            }
            catch { /* ignore */ }
            entry.src = this._ac.createMediaStreamSource(tapStream);
            entry.src.connect(this._mixGain);
            entry.connected = true;
            if (entry.timer) {
                clearTimeout(entry.timer);
                entry.timer = null;
            }
            logger.info(`[nkf-aec/farend] source ${id} tap LIVE (capture=${canCapture})`);
        };
        // Direct stream, or a captured track that is already flowing → wire now.
        // captureStream() audio tracks start muted and unmute when frames flow.
        if (!canCapture || (track && !track.muted)) {
            wire();
            return;
        }
        track.addEventListener('unmute', wire, { once: true });
        // Missed the race (unmute never came): rebuild the tap, bounded.
        entry.timer = setTimeout(() => {
            entry.timer = null;
            if (entry.connected) {
                return;
            }
            if (entry.retries++ < 5) {
                logger.warn(`[nkf-aec/farend] source ${id} tap silent — re-tap (${entry.retries})`);
                this._attachTap(id, entry);
            }
            else {
                logger.warn(`[nkf-aec/farend] source ${id} unmute never fired — raw stream fallback`);
                try {
                    entry.src?.disconnect();
                }
                catch { /* ignore */ }
                entry.src = this._ac.createMediaStreamSource(entry.stream);
                entry.src.connect(this._mixGain);
                entry.connected = true;
            }
        }, 1000);
    }
    /**
     * Reconcile the source map with the current redux track list.  Adds
     * sources for newly-arrived remote audio tracks, removes sources for
     * tracks that have left.
     */
    sync(tracks) {
        const remoteAudio = tracks.filter(t => !t.local && t.mediaType === MEDIA_TYPE.AUDIO && t.jitsiTrack);
        const presentIds = new Set();
        for (const t of remoteAudio) {
            const id = t.jitsiTrack.getId?.() ?? `${t.participantId}_${t.jitsiTrack.rtcId ?? ''}`;
            presentIds.add(id);
            if (this._sources.has(id))
                continue;
            const stream = t.jitsiTrack.getOriginalStream?.()
                ?? (t.jitsiTrack.getTrack ? new MediaStream([t.jitsiTrack.getTrack()]) : null);
            if (!stream) {
                logger.warn(`[nkf-aec/farend] track ${id} has no MediaStream`);
                continue;
            }
            try {
                // AUDIBLE <audio> at volume=1 that serves BOTH roles:
                //   (1) native playback of the remote audio (robust, OS-buffered
                //       — no crackle, unlike routing playback through
                //       captureStream→AudioContext→destination), and
                //   (2) the AEC far-end reference, tapped from the SAME element
                //       via captureStream() below.
                // Using one element for both makes far bit-identical (same
                // decode/clock) to what the user actually hears, which the NKF
                // adaptive filter needs to cancel: a SEPARATE silent tap element
                // gave only ~4-5 dB ERLE, while this same-element tap gives
                // ~20+ dB. (NOT muted — muted makes captureStream() silent on
                // Chrome.) AudioTracksContainer is bypassed for nkfAec so this is
                // the sole audible path (no double-play).
                const audioEl = document.createElement('audio');
                // 'element' tap mode: the element is MUTED — it only pulls the
                // remote stream so Chrome feeds samples to the
                // createMediaStreamSource tap; the audible path is
                // mixGain → ac.destination (same samples as the far reference).
                // 'capture' mode (default): the element IS the audible playback.
                audioEl.muted = this._tapMode === 'element';
                audioEl.volume = 1;
                audioEl.autoplay = true;
                audioEl.style.display = 'none';
                audioEl.srcObject = stream;
                document.body.appendChild(audioEl);
                // Do NOT tap+connect synchronously here: captureStream() called
                // before the element is decoding yields a permanently-silent
                // track (far-peak=0 → no cancellation → echo loop), and whether
                // it does is a race that resolves differently every join.
                // _attachTap() instead waits for the tapped track to carry
                // frames (unmute) and re-taps if it never does.
                const entry = {
                    src: null, audioEl, stream, connected: false, retries: 0, timer: null
                };
                this._sources.set(id, entry);
                this._attachTap(id, entry);
                logger.info(`[nkf-aec/farend] added remote source ${id} (tap pending)`);
            }
            catch (e) {
                logger.error(`[nkf-aec/farend] failed to add source ${id}`, e);
            }
        }
        for (const [id, entry] of this._sources) {
            if (presentIds.has(id))
                continue;
            if (entry.timer) {
                clearTimeout(entry.timer);
                entry.timer = null;
            }
            try {
                entry.src?.disconnect();
            }
            catch { /* ignore */ }
            try {
                entry.audioEl?.pause();
                if (entry.audioEl)
                    entry.audioEl.srcObject = null;
                entry.audioEl?.remove();
            }
            catch { /* ignore */ }
            this._sources.delete(id);
            logger.info(`[nkf-aec/farend] removed remote source ${id}`);
        }
    }
    /**
     * Resume the AC if it was suspended by the autoplay policy.  Safe to
     * call repeatedly.
     *
     * Chrome refuses to leave `suspended` unless resume() lives inside an
     * active user-gesture handler.  jitsi's conference init runs through
     * async middleware chains that lose gesture context, so the immediate
     * resume() returns a Promise that may stay pending forever.  Fall
     * back to resuming on the next user interaction.
     */
    async resume() {
        if (this._ac.state !== 'suspended')
            return;
        try {
            await this._ac.resume();
        }
        catch { /* ignore */ }
        if (this._ac.state !== 'suspended')
            return;
        if (typeof document === 'undefined')
            return;
        const handler = () => {
            this._ac.resume().catch(() => { });
            document.removeEventListener('click', handler, true);
            document.removeEventListener('keydown', handler, true);
            document.removeEventListener('touchstart', handler, true);
        };
        document.addEventListener('click', handler, true);
        document.addEventListener('keydown', handler, true);
        document.addEventListener('touchstart', handler, true);
        logger.info('[nkf-aec/farend] AC suspended; will resume on next user gesture');
    }
}
let _singleton = null;
let _unsubscribe = null;
/**
 * Get (or lazily create) the page-wide remote far-end mix.  On first call
 * subscribes to the redux store so the source set tracks the live track
 * list.  Idempotent.
 */
export function getRemoteFarEndMix(store) {
    if (_singleton)
        return _singleton;
    // 'capture' (default) preserves the long-standing PC behaviour; 'element'
    // is opt-in via config.js (nkfAec.farTap) for devices where captureStream
    // jitter breaks the far reference (some Androids).
    const tapMode = store.getState()['features/base/config']?.nkfAec?.farTap === 'element'
        ? 'element' : 'capture';
    logger.info(`[nkf-aec/farend] far tap mode: ${tapMode}`);
    const mix = new RemoteFarEndMix(tapMode);
    _singleton = mix;
    let lastTracks = null;
    const apply = () => {
        const tracks = store.getState()['features/base/tracks'];
        if (tracks === lastTracks)
            return;
        lastTracks = tracks;
        mix.sync(tracks);
    };
    apply();
    // Prefer real redux subscribe when available; otherwise poll.  Some
    // jitsi entry paths hand us a wrapped store that doesn't expose
    // subscribe(), and we can't risk crashing the whole effect setup.
    const sub = store.subscribe;
    if (typeof sub === 'function') {
        _unsubscribe = sub.call(store, apply);
    }
    else {
        const interval = setInterval(apply, 500);
        _unsubscribe = () => clearInterval(interval);
    }
    // Best-effort resume.  AudioContexts created without a user gesture may
    // start in 'suspended' on some browsers; we resume after the next user
    // interaction implicitly via Web Audio's autoplay handling.
    void mix.resume();
    return mix;
}
/**
 * Test/teardown helper.  Not used in production.
 */
export function _resetRemoteFarEndMix() {
    if (_unsubscribe) {
        try {
            _unsubscribe();
        }
        catch { /* ignore */ }
        _unsubscribe = null;
    }
    _singleton = null;
}
